import { Chart } from '@antv/g2';
import { DataSet } from '@antv/data-set';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';
import { renderTooltip } from '../../../../helpers/Tooltip';

const colors = ['#6C737F', '#6AD99E', '#2E90FA']

export const WIDTH = 500;

export function usePreInvestement({ data }) {
  const container = useRef();
  const { t } = useTranslation();
  const [graphData, setGraphData] = useState(data);
  const [mainGraph, setMainGraph] = useState(null);
  const [graphWidth, setGraphWidth] = useState(0);

  const observer = useRef(new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      const { width } = entries[0].contentRect;
      setGraphWidth(width);
    });
  }));

  useEffect(() => {
    if (mainGraph && (mainGraph.width !== graphWidth) && graphWidth) {
      let gSize = graphWidth;
      if (gSize > WIDTH) {
        gSize = WIDTH;
      }

      let gHeight = gSize;
      mainGraph.changeSize(gSize, gHeight);

      mainGraph.axis('label', {
        line: null,
        tickLine: null,
        label: {
          autoHide: true,
          style: {
            fontSize: 0,
          },
        },
        grid: {
          line: {
            style: {
              lineDash: null,
            },
          },
        },
      });
      mainGraph.axis('score', {
        line: null,
        tickLine: null,
        label: {
          style: {
            fontSize: 0,
          },
        },
        grid: {
          line: {
            type: 'line',
            style: {
              lineDash: null,
            },
          },
        },
      });
      mainGraph.render(true);
    }
  }, [graphWidth, mainGraph]);

  useEffect(() => {
    setGraphData(data);
  }, [data]);

  useEffect(() => {
    if (container.current) {
      observer.current.observe(container.current);
    }
    return () => {
      if (container.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.current.unobserve(container.current);
      }
    }
  }, [container.current, observer]);

  const getHeight = () => {
    return container.current.offsetHeight
  };

  useEffect(() => {
    if (container.current) {
      const { DataView } = DataSet;
      const dv = new DataView().source(data);
      dv.transform({
        type: 'fold',
        fields: ['Comparison', 'Baseline', 'Current'],
        key: 'user',
        value: 'score',
      });
      if (mainGraph) {
        mainGraph.data(dv.rows);
        mainGraph.tooltip({
          shared: true,
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: {
                lineDash: [4, 4],
                stroke: '#333'
              }
            }
          },
          customContent: (title, data) => {
            let content = '';
            let _t = '';

            if (data && data.length) {
              _t = t(data[0].data.label);
              content = renderTooltip({
                title: _t,
                items: data.map((d) => ({
                  color: d.mappingData.color,
                  label: t(d.name),
                  value: d.value ? Number(d.value) : '-',
                }))
              })
            }

            return content;
          }
        });
        mainGraph.render(true);
      } else {
        const chart = new Chart({
          container: container.current,
          // autoFit: true,
          height: getHeight(),
          width: container.current.offsetWidth,
          // renderer: 'svg',
        });
        chart.data(dv.rows);
        chart.scale('score', {
          min: 0,
          max: 6,
          showLast: false,
          sync: true,
        });
        chart.coordinate('polar', {
          radius: 0.8,
        });
        chart.tooltip({
          shared: true,
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: {
                lineDash: [4, 4],
                stroke: '#333'
              }
            }
          },
          customContent: (title, data) => {
            let content = '';
            let _t = '';

            if (data && data.length) {
              _t = t(data[0].data.label);
              content = renderTooltip({
                title: _t,
                items: data.map((d) => ({
                  color: d.mappingData.color,
                  label: t(d.name),
                  value: d.value ? Number(d.value) : '-',
                }))
              })
            }

            return content;
          }
        });
        chart.axis('label', {
          line: null,
          tickLine: null,
          label: {
            style: {
              fontSize: 0,
            },
          },
          grid: {
            line: {
              style: {
                lineDash: null,
              },
            },
          },
        });
        chart.axis('score', {
          line: null,
          tickLine: null,
          label: {
            style: {
              fontSize: 12,
            },
          },
          grid: {
            line: {
              type: 'line',
              style: {
                lineDash: null,
              },
            },
          },
        });
        chart.legend(false);
        chart.annotation(false);
        chart
          .line()
          .position('label*score')
          .color('user', colors)
          .size(2);
        chart
          .point()
          .position('label*score')
          .shape('circle')
          .color('user', colors)
          .size(3)
          .style({
            stroke: '#fff',
            lineWidth: 1,
            fillOpacity: 1,
          });
        chart
          .area()
          .position('label*score')
          .color('user', colors);
        chart.render();
        setMainGraph(chart);
      }
    }
  }, [container, graphData, t])

  return {
    graphWidth,
    container,
  };
}
